import { motion } from 'framer-motion';
import styled from 'styled-components';

import {
    designColor,
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledInteraction,
    styledReset,
    styledTypography,
} from '@pkg/primitives';

interface StyledItemProps {
    $disabled?: boolean;
}

export const StyledSlidingSelector = styled.ul`
    ${styledReset.list};
    --sliding-selector-color: rgb(255, 255, 255, 0.4);
    --sliding-selector-meta-color: ${designColor.raisinDark};
    --sliding-selector-meta-bg-color: rgb(255, 255, 255, 0.4);
    display: flex;
    gap: 4px;
`;

export const StyledItem = styled(motion.li)<StyledItemProps>`
    // This element cannot be assigned a z-index!
    // Doing so will cause the "active background" to sit on top
    // of its "previous item" when moving to a sibling active item.
    position: relative;
    flex: 0 0 auto;
    opacity: ${({ $disabled }) => ($disabled ? 0.8 : 1)};
    transition: opacity ${duration.normal}ms ${easing.cubic};
`;

export const ActiveBackground = styled(motion.div)`
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 999px;
    background-color: rgb(255, 255, 255, 1);
`;

export const Action = styled.button.attrs({ type: 'button' })`
    ${styledReset.button};
    z-index: 2;
    position: relative;
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 999px;
    outline: 2px solid transparent !important;
    outline-offset: 2px;
    transition: outline-color ${duration.normal}ms ${easing.cubic};

    &[disabled] {
        ${styledInteraction.interactionDisable};
    }

    &[aria-pressed] {
        &,
        &:hover,
        &:active {
            --sliding-selector-color: ${designColor.raisinDark};
            --sliding-selector-meta-color: rgb(255, 255, 255, 1);
            --sliding-selector-meta-bg-color: ${designColor.redLight};
        }
    }

    &:focus-visible {
        outline-color: ${designColor.brandPurple} !important;
    }

    &:hover {
        --sliding-selector-color: rgb(255, 255, 255, 1);
        --sliding-selector-meta-bg-color: ${designColor.redLight};
    }

    &:active {
        --sliding-selector-color: rgb(255, 255, 255, 0.6);
    }
`;

export const Label = styled.span`
    ${styledTypography.allianceBase};
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: var(--sliding-selector-color);
    letter-spacing: -0.02em;
    transition: color ${duration.slow}ms ${easing.cubic};
`;

export const MetaLabel = styled.span`
    ${styledTypography.allianceBase};
    display: block;
    position: relative;
    padding-left: 4px;
    padding-right: 4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--sliding-selector-meta-color);
    transition: color ${duration.slow}ms ${easing.cubic};

    // Background is a psuedo element to help prevent creating
    // more height than the <Label />. Top/bottom offset is
    // unequal in order to improve horizontal alignment.
    &::before {
        content: '';
        position: absolute;
        z-index: -1;
        top: -3px;
        bottom: -4px;
        left: 0;
        right: 0;
        display: block;
        background-color: var(--sliding-selector-meta-bg-color);
        border-radius: 4px;
        transition: background-color ${duration.slow}ms ${easing.cubic};
    }
`;
